import { IProject, speciesModelCodeFor, speciesDefaultsFor } from "./types/IProject";
import { IStructure } from "./types/IStructure";
import { IArea, startDayFor, modelCodeFor } from './types/IArea';
import { AreaGridUtils } from './util/AreaGridUtils';
import { AquaFishFacade } from './c/AquaFishFacade';
import { AquaShellFacade } from './c/AquaShellFacade';
import { IDriverset } from './api/models';
import { ISpecies, isShellfishModel } from './api/models/ISpecies';
import { IOrganixGridPoint } from './types/IOrganixGridPoint';
import { IOrganixInput } from './types/IOrganixInput';

// The growth run's inputs, common to AquaFish and AquaShell.
interface IGrowthParams {
  startTime: number;
  endTime: number;
  species: number;
  initialWeight?: number;
}

/**
 * Exports a calculated area grid to JSON.
 */
class GridExporterJSON {

  private static finfishLoads = async (params: IGrowthParams, driverset: IDriverset): Promise<IOrganixInput['loads']> => {
    const result = await AquaFishFacade.call(params, driverset);
    return {
      wasteFeedLoad: result.days.map(d => d.wasteFeed),
      faecesLoad: result.days.map(d => d.faecesElimination)
    };
  }

  /**
   * Shellfish have no waste feed. ORGANIX takes their pseudofaeces in
   * wasteFeedLoad instead; the array keeps its name for both. AquaShell's POM
   * biodeposition is faeces + pseudofaeces (TAquaShell::Feed), so pseudofaeces
   * is the difference. Both are POM (DW) per animal per day, like AquaFish's.
   * Clamped at 0 against rounding.
   */
  private static shellfishLoads = async (params: IGrowthParams, driverset: IDriverset): Promise<IOrganixInput['loads']> => {
    const result = await AquaShellFacade.call(params, driverset);
    return {
      wasteFeedLoad: result.days.map(d => Math.max(0, d.pomBiodeposition - d.faecesBiodeposition)),
      faecesLoad: result.days.map(d => d.faecesBiodeposition)
    };
  }

  static run = async (project: IProject, driverset: IDriverset, area: IArea, modelCode: number, structures: IStructure[], data: IOrganixGridPoint[], species: ISpecies[] = []): Promise<IOrganixInput> => {
    const width_meters = AreaGridUtils.width(area.rect);
    const height_meters = AreaGridUtils.height(area.rect);
    const horizontal_resolution = area.resolution;
    const vertical_resolution = AreaGridUtils.verticalResolution(area.rect, area.resolution);

    // One code drives both the growth run and the metadata ORGANIX receives.
    const speciesCode = speciesModelCodeFor(project);
    const isPathogens = modelCode === modelCodeFor('pathogens');

    const growthParams = {
      startTime: startDayFor(area.startMonth), endTime: area.days - 1, species: speciesCode,
      // null leaves the facade on its own default, as before this column existed.
      initialWeight: speciesDefaultsFor(project, species).initialWeight ?? undefined
    };
    const loads = isShellfishModel(speciesCode)
      ? await GridExporterJSON.shellfishLoads(growthParams, driverset)
      : await GridExporterJSON.finfishLoads(growthParams, driverset);

    const input: IOrganixInput = {
      metadata: {
        farmName: project.name,
        species: speciesCode,
        matrixWidth: horizontal_resolution,
        matrixHeight: vertical_resolution,
        cellWidth: width_meters / horizontal_resolution,
        cellHeight: height_meters / vertical_resolution,
        structures: structures.length,
        stocking: parseInt(project.stocking as any),
        // A pathogen run's period is the emission period, not the culture
        // length. The loads below still cover area.days: the pathogen model
        // ignores them, but ORGANIX requires them and they must not run short.
        cultureDays: isPathogens ? area.pathogenEmissionPeriod : area.days,
        structureDepth: area.structureDepth,
        feedPelletDiameter: area.feedPelletDiameter,
        faecesDiameter: area.faecesDiameter,
        xDispersion: area.xDispersion,
        yDispersion: area.yDispersion,
        useFinfish: !isShellfishModel(speciesCode),
        ...(isPathogens && {
          pathogenDecay: area.pathogenDecay,
          pathogenVerticalDispersion: area.pathogenVerticalDispersion
        })
      },
      grid: data,
      structures: structures.map((s, i) => ({ id: i + 1, name: s.name, stocking: parseInt(s.stocking as any) })),
      loads: loads,
      solver: {
        solver: area.solver,
        timestepReductionFactor: area.timestepReductionFactor,
        adamsBashforthIterations: 1,
        turbulenceCoefficient: 1.0,
        modelCode: modelCode
      }
    }

    return input;
  }
}

export { GridExporterJSON }
